/**
 * Lightweight fetch wrapper s uniform error mapping per REQ-API-012.
 *
 * - CSRF token z `axigon_csrf` cookie přidán do `X-CSRF-Token` header pro
 *   write methods (POST/PUT/PATCH/DELETE).
 * - `credentials: 'include'` → SameSite=strict HttpOnly access token cookie
 *   se posílá automaticky pro /api/* (same-origin v dev + prod per Vite
 *   proxy v `vite.config.ts`).
 * - 429 odpověď s `Retry-After` header → throw `RateLimitedError` s parsed
 *   delay (per REQ-API-012, REQ-AUTH-007 surface neutrality).
 * - 401 → throw `UnauthorizedError` (UI vrstva chytí a re-routuje na /login).
 */

export interface ApiErrorBody {
  error_code?: string;
  message?: string;
  // Server volitelně pošle více polí — drží se otevřeně.
  [key: string]: unknown;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: ApiErrorBody;
  public readonly errorCode: string;
  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.errorCode = body.error_code ?? `HTTP_${status}`;
  }
}

export class RateLimitedError extends ApiError {
  public readonly retryAfterSeconds: number;
  constructor(body: ApiErrorBody, retryAfterSeconds: number) {
    super(429, body);
    this.name = "RateLimitedError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export class UnauthorizedError extends ApiError {
  constructor(status: number, body: ApiErrorBody) {
    super(status, body);
    this.name = "UnauthorizedError";
  }
}

/**
 * Globální handler pro expirovanou/missing session. Volá se z [apiCall] na 401
 * z business endpointů (vyjma auth-flow paths jako /api/public/auth/* a
 * /api/auth/me, kde 401 je legitimní součást vlastního flow).
 *
 * Registruje se v `main.tsx` při bootstrap aplikace. Tests handler neregistrují
 * → 401 v testech jen vyhodí [UnauthorizedError] bez side effectu.
 */
export type SessionExpiredHandler = (currentPath: string) => void;
let sessionExpiredHandler: SessionExpiredHandler | null = null;

export function setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
  sessionExpiredHandler = handler;
}

function isAuthFlowPath(path: string): boolean {
  return path.startsWith("/api/public/auth/") || path === "/api/auth/me";
}

/**
 * Thrown synchronously for destructive (POST/PUT/PATCH/DELETE) calls fired
 * while `navigator.onLine === false`. UI mutation hooks catch it and render
 * an inline "offline" error on the action button rather than letting the
 * fetch fail later with a confusing network error (REQ-UI-023).
 */
export class OfflineError extends ApiError {
  constructor() {
    super(0, { error_code: "OFFLINE", message: "Offline — akce není k dispozici" });
    this.name = "OfflineError";
  }
}

function readCsrfCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)axigon_csrf=([^;]+)/);
  return match?.[1] ?? null;
}

let csrfBootstrap: Promise<void> | null = null;

/**
 * Pokud `axigon_csrf` cookie chybí, GET /api/public/auth/csrf-token aby ji
 * backend vystavil. Jedno-flight: paralelní callery sdílí stejný promise.
 * Failuje silně (chybu spolkne) — pokud bootstrap fail, follow-up POST stejně
 * vrátí 403 CSRF_MISSING, který UI správně zobrazí.
 */
async function ensureCsrfCookie(): Promise<void> {
  if (readCsrfCookie()) return;
  if (csrfBootstrap) {
    await csrfBootstrap;
    return;
  }
  csrfBootstrap = (async () => {
    try {
      await fetch("/api/public/auth/csrf-token", { credentials: "include" });
    } catch {
      // best-effort — network fail spolkneme, real chybu pak vyhodí real POST.
    } finally {
      csrfBootstrap = null;
    }
  })();
  await csrfBootstrap;
}

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export interface ApiCallOptions extends RequestInit {
  /** Pokud `false`, nebudeme přidávat X-CSRF-Token. */
  csrf?: boolean;
}

/**
 * `apiCall<T>` — typovaný fetch s automatickým mapováním errorů.
 *
 * Příklad:
 *   const session = await apiCall<LoginResponse>('/api/public/auth/login', {
 *     method: 'POST',
 *     body: JSON.stringify({ email, password }),
 *   });
 */
export async function apiCall<T>(path: string, options: ApiCallOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers ?? {});
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  // FormData → necháme browser dosadit Content-Type s multipart boundary.
  // Bez tohoto by `multipart/form-data; boundary=…` přepsalo na `application/json`
  // a backend by parser odmítl (chybějící boundary).
  if (options.body && !headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (WRITE_METHODS.has(method)) {
    // REQ-UI-023 — destructive actions while offline fail fast inline so
    // the UI can render a button-local error instead of a confusing network
    // error after the request times out.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new OfflineError();
    }
    if (options.csrf !== false) {
      await ensureCsrfCookie();
      const csrf = readCsrfCookie();
      if (csrf) headers.set("X-CSRF-Token", csrf);
    }
  }
  const response = await fetch(path, {
    ...options,
    credentials: "include",
    headers,
  });
  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
  const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
  if (response.status === 429) {
    const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
    throw new RateLimitedError(body, retryAfter);
  }
  if (response.status === 401) {
    // Globální session-expired handler — registrován v main.tsx.
    // Neprobíhá pro auth-flow paths (login/me/refresh), aby login form
    // nepřesměroval na /login při špatném heslu.
    if (sessionExpiredHandler && !isAuthFlowPath(path)) {
      // Idempotence — handler zavoláme nejvýš jednou. Mezi window.location.assign()
      // a skutečnou navigací je race window, ve kterém další 401 callery (SSE
      // pull / paralelní requesty) jinak handler volají opakovaně.
      const handler = sessionExpiredHandler;
      sessionExpiredHandler = null;
      const currentPath =
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : "/";
      handler(currentPath);
      // Krátký mezičas mezi throw a window.location.assign — kdyby UI mezi
      // tím chytlo error a renderovalo `error.message`, nesmí ukázat backend
      // text typu "Missing Bearer token". Přepíšeme na user-friendly hlášku.
      throw new UnauthorizedError(response.status, {
        error_code: body.error_code,
        message: "Přesměrovávám na přihlášení…",
      });
    }
    throw new UnauthorizedError(response.status, body);
  }
  throw new ApiError(response.status, body);
}

function parseRetryAfter(header: string | null): number {
  if (!header) return 60;
  const n = Number(header);
  if (Number.isFinite(n) && n > 0) return n;
  const date = Date.parse(header);
  if (!Number.isNaN(date)) {
    const seconds = Math.ceil((date - Date.now()) / 1000);
    return seconds > 0 ? seconds : 60;
  }
  return 60;
}
